/** TradeUnionDashboard — the archetype-aware admin dashboard.
 *
 *  Route: `/trade-union-dashboard/:slug`
 *
 *  When a union admin logs in they land here for their own union.
 *  The layout renders:
 *    · Header — union brand + archetype label
 *    · Applications inbox summary chip
 *    · Messages inbox summary chip
 *    · Tabs — one per widget in the archetype's `extra_widgets` bundle,
 *      each tab shows a `<WidgetTable>` fetched from the backend schema.
 *
 *  Non-admins hitting this route get bounced to the public profile.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import WidgetTable from "@/components/WidgetTable";
import {
  Building2, ArrowLeft, Inbox, UserPlus, LayoutGrid,
} from "lucide-react";

export default function TradeUnionDashboard() {
  const { slug } = useParams();
  const [cfg, setCfg]         = useState(null);
  const [msgs, setMsgs]       = useState({ count: 0, unread: 0 });
  const [apps, setApps]       = useState({ count: 0, pending: 0 });
  const [active, setActive]   = useState("");
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get(`/trade-unions/${slug}/dashboard/config`)
      .then(({ data }) => {
        setCfg(data);
        setActive((data.widget_bundle || []).find((w) => data.widget_schemas[w]) || "");
      })
      .catch((e) => {
        setError(e?.response?.status === 403
          ? "You don't have permission to manage this union."
          : e?.response?.data?.detail || "Failed to load dashboard.");
      });
    api.get(`/trade-unions/${slug}/messages`)
      .then(({ data }) => setMsgs(data))
      .catch(() => {});
    api.get(`/trade-unions/${slug}/applications`)
      .then(({ data }) => setApps(data))
      .catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center"
            data-testid="union-dash-error">
        <div className="text-white/60 text-sm">{error}</div>
        <Link to={`/trade-union/${slug}`}
              className="mt-4 inline-block text-[#F5B82E] text-xs uppercase tracking-[0.22em]">
          Back to public profile
        </Link>
      </div>
    );
  }

  if (!cfg) {
    return <div className="mx-auto max-w-3xl px-6 py-24 text-white/40 text-sm">Loading…</div>;
  }

  const bundle       = cfg.widget_bundle || [];
  const schemas      = cfg.widget_schemas || {};
  const widgetSlugs  = bundle.filter((w) => schemas[w]);
  const activeSchema = schemas[active];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10"
          data-testid="trade-union-dashboard-page">

      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <Link to={`/trade-union/${slug}`}
              className="w-10 h-10 rounded-full bg-white/[0.04] hover:bg-white/[0.08] flex items-center justify-center border border-white/10 transition"
              data-testid="union-dash-back">
          <ArrowLeft size={14}/>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
            Trade Union Dashboard
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl uppercase tracking-tight truncate">
            {cfg.union.brand_name}
          </h1>
          <div className="mt-1 text-[11px] text-white/50">
            <Building2 size={11} className="inline mr-1"/> {cfg.union.union_type_label}
          </div>
        </div>
      </div>

      {/* Inbox summary chips */}
      <div className="mt-6 flex flex-wrap gap-3">
        <SummaryChip
          icon={<Inbox size={14}/>}
          label="Messages"
          primary={msgs.unread}
          secondary={`${msgs.count} total`}
          accent="#22D3EE"
          testid="union-dash-messages-chip"/>
        <SummaryChip
          icon={<UserPlus size={14}/>}
          label="Applications"
          primary={apps.pending}
          secondary={`${apps.count} total`}
          accent="#10B981"
          testid="union-dash-applications-chip"/>
        <SummaryChip
          icon={<LayoutGrid size={14}/>}
          label="Archetype Widgets"
          primary={widgetSlugs.length}
          secondary={cfg.union.union_type_label}
          accent="#F5B82E"
          testid="union-dash-widgets-chip"/>
      </div>

      {/* Widget tab bar */}
      {widgetSlugs.length === 0 ? (
        <div className="mt-8 cs-card rounded-2xl p-8 text-center text-white/50 text-sm"
              data-testid="union-dash-empty">
          No archetype widgets defined for this union type.
        </div>
      ) : (
        <>
          <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-2"
                data-testid="union-dash-tabs">
            {widgetSlugs.map((w) => {
              const isActive = w === active;
              return (
                <button key={w} onClick={() => setActive(w)}
                        className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] transition border ${
                          isActive
                            ? "border-[#F5B82E] text-[#F5B82E] bg-[#F5B82E]/8"
                            : "border-white/10 text-white/60 hover:text-white/90 hover:border-white/25"
                        }`}
                        data-testid={`union-dash-tab-${w}`}>
                  {schemas[w].label}
                </button>
              );
            })}
          </div>

          <div className="mt-6">
            {activeSchema && (
              <WidgetTable slug={slug} widget_type={active} schema={activeSchema} />
            )}
          </div>
        </>
      )}
    </div>
  );
}


function SummaryChip({ icon, label, primary, secondary, accent, testid }) {
  return (
    <div className="cs-card rounded-2xl px-4 py-3 flex items-center gap-3"
          style={{ borderColor: `${accent}33` }}
          data-testid={testid}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${accent}1A`, color: accent }}>
        {icon}
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/50">
          {label}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-display" style={{ color: accent }}>
            {primary}
          </span>
          <span className="text-[10px] text-white/45">{secondary}</span>
        </div>
      </div>
    </div>
  );
}
